/** UI copy for Hawk (sidebar + moderator dashboard). Keys are flat dotted paths. */

import { MODERATOR_PAGE_MESSAGES } from './moderatorPages';

export type UiLocale = 'de' | 'en' | 'vi' | 'fr';

export const LOCALE_BCP47: Record<UiLocale, string> = {
  en: 'en-GB',
  de: 'de-DE',
  vi: 'vi-VN',
  fr: 'fr-FR',
};

const en: Record<string, string> = {
  'sidebar.brand': 'Hawk',
  'sidebar.logOut': 'Log out',
  'sidebar.expandSidebar': 'Expand sidebar',
  'sidebar.collapseSidebar': 'Collapse sidebar',
  'sidebar.language': 'Language',

  'nav.dashboard': 'Dashboard',
  'nav.moderators': 'Moderators',
  'nav.settings': 'Usages Settings',
  'nav.aiManager': 'AI Manager',
  'nav.mailOverview': 'Mail Overview',
  'nav.scraperPool': 'Scraper Pool',
  'nav.dataPool': 'Data Pool',
  'nav.subscribers': 'Subscribers',
  'nav.emailTemplates': 'Email Templates',
  'nav.myNewsletters': 'My Newsletters',
  'nav.dataroomSetting': 'Dataroom Setting',
  'nav.linkedin': 'LinkedIn',
  'nav.linkedinScheduled': 'LinkedIn Scheduled',
  'nav.section.administration': 'Administration',
  'nav.section.editorial': 'Editorial',
  'nav.section.yourAccount': 'Your account',

  'locale.en': 'English',
  'locale.de': 'German',
  'locale.vi': 'Vietnamese',
  'locale.fr': 'French',

  'role.admin': 'Admin',
  'role.moderator': 'Moderator',
  'role.subscriber': 'Subscriber',

  'dashboard.title': 'Dashboard',
  'dashboard.subtitle': 'Scrape → Collect → AI Generate → Deliver',
  'dashboard.pipeline': 'Pipeline',
  'dashboard.progress': 'Progress',
  'dashboard.stepOf': 'Step {n} of {total}',
  'dashboard.triggerDrafts': 'Trigger drafts',
  'dashboard.requestFailed': 'Request failed',
  'dashboard.badgeScheduled': 'Scheduled {time}',
  'dashboard.badgeScraperRunning': 'Scraper running',
  'dashboard.badgeAnalyzing': 'Analyzing articles',
  'dashboard.badgeCheckingReadiness': 'Checking subscriber readiness',
  'dashboard.badgeAutoCheckArmed': 'Auto-check armed',
  'dashboard.badgeEmailsReady': 'Emails ready',

  'dashboard.step': 'Step {n}',
  'dashboard.scraperPool.title': 'Scraper Pool',
  'dashboard.scraperPool.desc': 'Runs automatically at {time} daily',
  'dashboard.scraperPool.activeUrls': 'Active URLs',
  'dashboard.scraperPool.scheduled': 'Scheduled',
  'dashboard.scraperPool.daily': '{time} daily',
  'dashboard.scraperPool.lastRun': 'Last run',
  'dashboard.scraperPool.today': 'Today {time}',
  'dashboard.scraperPool.more': '+{count} more',
  'dashboard.scraperPool.noSources': 'No active sources yet',
  'dashboard.scraperPool.scraping': 'Scraping {count} articles…',
  'dashboard.scraperPool.completed': 'Completed — {count} articles collected',
  'dashboard.scraperPool.manage': 'Manage',
  'dashboard.scraperPool.runNow': 'Run now',

  'dashboard.dataPool.title': 'Data Pool',
  'dashboard.dataPool.desc': 'Collected articles',
  'dashboard.dataPool.articlesToday': 'Articles today',
  'dashboard.dataPool.pendingAnalysis': 'Pending analysis',
  'dashboard.dataPool.lastRun': 'Last run',
  'dashboard.dataPool.activeTopics': 'Active topics',
  'dashboard.dataPool.articlesCount': '{count} articles',
  'dashboard.dataPool.viewArticles': 'View articles',
  'dashboard.dataPool.runAnalysis': 'Run analysis',
  'dashboard.dataPool.runAnalysisTitle':
    "Run AI analysis on today's already-scraped articles without re-scraping",

  'dashboard.aiGen.title': 'AI generation',
  'dashboard.aiGen.desc': 'Checks subscriber readiness regularly',
  'dashboard.aiGen.subscribers': 'Subscribers',
  'dashboard.aiGen.waitingDrafts': 'Waiting for fresh articles to trigger draft checks',
  'dashboard.aiGen.waitingScraper': 'Waiting for scraper to finish…',
  'dashboard.aiGen.runningAnalysis': 'Running AI analysis on existing articles…',
  'dashboard.aiGen.autoCheckEvery': 'Auto-check',
  'dashboard.aiGen.lastCheck': 'Last check {time}',
  'dashboard.aiGen.autoCheckActive': 'Auto-check active',
  'dashboard.aiGen.autoCheckIdle': 'Auto-check idle',
  'dashboard.aiGen.pending': '{count} pending',
  'dashboard.aiGen.noReadiness': 'No readiness data yet',
  'dashboard.aiGen.sentToday': 'Sent today',
  'dashboard.aiGen.draftReady': 'Draft ready',
  'dashboard.aiGen.pendingGeneration': 'Pending generation',
  'dashboard.aiGen.noTopics': 'No topics',
  'dashboard.aiGen.allReady': 'All {count} subscribers are ready for delivery',
  'dashboard.aiGen.someNeedDrafts': '{count} subscribers still need drafts',
  'dashboard.aiGen.pendingShort': 'Pending',

  'dashboard.delivery.title': 'Email delivery',
  'dashboard.delivery.desc': 'Send to subscribers',
  'dashboard.delivery.sendAll': 'Send all',
  'dashboard.delivery.individual': 'Individual',
  'dashboard.delivery.activeSubscribers': 'Active subscribers',
  'dashboard.delivery.lastDelivery': 'Last delivery',
  'dashboard.delivery.pendingSend': 'Pending send',
  'dashboard.delivery.readyCount': '{count} ready',
  'dashboard.delivery.noSubscribers': 'No subscribers found',
  'dashboard.delivery.sent': '✓ Sent',
  'dashboard.delivery.ready': '● Ready',
  'dashboard.delivery.moreSubscribers': '+{count} more subscribers',
  'dashboard.delivery.searchPlaceholder': 'Search…',
  'dashboard.delivery.selectAll': 'All',
  'dashboard.delivery.sendAllNow': 'Send all now',
  'dashboard.delivery.sendToOne': 'Send to 1 subscriber',
  'dashboard.delivery.sendToMany': 'Send to {count} subscribers',

  'dashboard.statSubscribers': 'Total subscribers',
  'dashboard.statArticlesToday': 'Articles today',
  'dashboard.statActiveTopics': 'Active topics',
  'dashboard.statPendingAnalysis': 'Pending analysis',

  'dashboard.recentTitle': 'Recent pipeline activity',
  'dashboard.recentDesc': 'Latest events across scrape, AI generation, and delivery',

  'dashboard.activityScraperRun': 'Scraper run {status} — {count} articles collected',

  'dashboard.toastSendAll': 'Draft send-all: {sent} sent{failed}{skipped}.',
  'dashboard.toastFailed': ', {n} failed',
  'dashboard.toastSkipped': ', {n} skipped',
  'dashboard.toastSelectOne': 'Select at least one subscriber.',
  'dashboard.toastSentToMany': 'Draft newsletter sent to {count} subscriber(s).',

  'settings.title': 'Settings',
  'settings.subtitle': 'Scraper limit configuration by client contract.',
  'settings.clientLabel': 'Moderator / client',
  'settings.clientEditing': 'Editing contract limits for #{id}',
  'settings.moderatorsTitle': 'Moderators',
  'settings.moderatorConfigTitle': 'Moderator Configuration',
  'settings.configTitle': 'Scraper Limit Settings',
  'settings.sectionAutoQuota': 'Automatic limits',
  'settings.sectionAutoQuotaDesc': 'Configure automatic runs and extra runs per period.',
  'settings.sectionDataLimits': 'Data limits',
  'settings.sectionDataLimitsDesc': 'Limit articles crawled per URL.',
  'settings.sectionStatusNotes': 'Status & notes',
  'settings.sectionStatusNotesDesc': 'Enable or disable the scraper and note why settings changed.',
  'settings.searchPlaceholder': 'Search by name or email…',
  'settings.noSearchResults': 'No moderators match your search.',
  'settings.selectModerator': 'Select a moderator to configure scraper limits.',
  'settings.statusActive': 'Active',
  'settings.statusInactive': 'Inactive',
  'settings.clientMeta': 'Client #{id}',
  'settings.autoRunsPerDay': 'Auto runs / day',
  'settings.extraRunsPerPeriod': 'Extra runs / period',
  'settings.extraPeriod': 'Extra period',
  'settings.maxArticlesPerUrl': 'Max articles / URL (1–200)',
  'settings.maxUrlsPerRun': 'Max URLs / moderator run',
  'settings.scraperEnabled': 'Scraper enabled',
  'settings.scraperEnabledDesc': 'Allow this client to run the scraper at all.',
  'settings.changeReason': 'Change reason',
  'settings.changeReasonPlaceholder': 'Contract amendment…',
  'settings.saveConfig': 'Save configuration',
  'settings.cancel': 'Cancel',
  'settings.toastConfigSaved': 'Scraper limits saved',
  'settings.errMaxArticles': 'Maximum articles per URL must be between 1 and 200.',
  'settings.loadFailed': 'Failed to load clients',
  'settings.noClients': 'No clients found yet. Add a moderator first.',
  'settings.optional': '(optional)',
  'settings.helpAutoRuns': 'Scheduled automatic scrape jobs allowed each calendar day.',
  'settings.helpExtraRuns': 'Additional manual runs available within the selected period.',
  'settings.helpExtraPeriod': 'Reset window for extra run allowance.',
  'settings.helpMaxUrls': 'Upper bound on URLs submitted in a single moderator run.',
  'settings.helpMaxArticles': 'Cap on articles crawled per URL. Must be between 1 and 200.',
  'settings.helpChangeReason': 'Optional note stored with this configuration change.',
  'settings.period.daily': 'Daily',
  'settings.period.weekly': 'Weekly',
  'settings.period.monthly': 'Monthly',
  'settings.paginationRange': '{start}–{end} of {total}',
  'settings.prevPage': 'Previous page',
  'settings.nextPage': 'Next page',

};

const de: Record<string, string> = {
  'sidebar.brand': 'Hawk',
  'sidebar.logOut': 'Abmelden',
  'sidebar.expandSidebar': 'Seitenleiste aufklappen',
  'sidebar.collapseSidebar': 'Seitenleiste einklappen',
  'sidebar.language': 'Sprache',

  'nav.dashboard': 'Übersicht',
  'nav.moderators': 'Moderatoren',
  'nav.settings': 'Usages Settings',
  'nav.aiManager': 'KI-Manager',
  'nav.mailOverview': 'E-Mail-Übersicht',
  'nav.scraperPool': 'Scraper-Pool',
  'nav.dataPool': 'Datenpool',
  'nav.subscribers': 'Abonnenten',
  'nav.emailTemplates': 'E-Mail-Vorlagen',
  'nav.myNewsletters': 'Meine Newsletter',
  'nav.dataroomSetting': 'Dataroom-Einstellungen',
  'nav.linkedin': 'LinkedIn',
  'nav.linkedinScheduled': 'LinkedIn geplant',
  'nav.section.administration': 'Administration',
  'nav.section.editorial': 'Redaktion',
  'nav.section.yourAccount': 'Ihr Konto',

  'locale.en': 'Englisch',
  'locale.de': 'Deutsch',
  'locale.vi': 'Vietnamesisch',
  'locale.fr': 'Französisch',

  'role.admin': 'Admin',
  'role.moderator': 'Moderator',
  'role.subscriber': 'Abonnent',

  'dashboard.title': 'Übersicht',
  'dashboard.subtitle': 'Scraping → Sammeln → KI → Versand',
  'dashboard.pipeline': 'Pipeline',
  'dashboard.progress': 'Fortschritt',
  'dashboard.stepOf': 'Schritt {n} von {total}',
  'dashboard.triggerDrafts': 'Entwürfe auslösen',
  'dashboard.requestFailed': 'Anfrage fehlgeschlagen',
  'dashboard.badgeScheduled': 'Geplant {time}',
  'dashboard.badgeScraperRunning': 'Scraper läuft',
  'dashboard.badgeAnalyzing': 'Artikel werden analysiert',
  'dashboard.badgeCheckingReadiness': 'Abonnenten-Status wird geprüft',
  'dashboard.badgeAutoCheckArmed': 'Auto-Check aktiv',
  'dashboard.badgeEmailsReady': 'E-Mails bereit',

  'dashboard.step': 'Schritt {n}',
  'dashboard.scraperPool.title': 'Scraper-Pool',
  'dashboard.scraperPool.desc': 'Täglich automatisch um {time}',
  'dashboard.scraperPool.activeUrls': 'Aktive URLs',
  'dashboard.scraperPool.scheduled': 'Geplant',
  'dashboard.scraperPool.daily': '{time} täglich',
  'dashboard.scraperPool.lastRun': 'Letzter Lauf',
  'dashboard.scraperPool.today': 'Heute {time}',
  'dashboard.scraperPool.more': '+{count} weitere',
  'dashboard.scraperPool.noSources': 'Noch keine aktiven Quellen',
  'dashboard.scraperPool.scraping': 'Scrape {count} Artikel…',
  'dashboard.scraperPool.completed': 'Fertig — {count} Artikel gesammelt',
  'dashboard.scraperPool.manage': 'Verwalten',
  'dashboard.scraperPool.runNow': 'Jetzt starten',

  'dashboard.dataPool.title': 'Datenpool',
  'dashboard.dataPool.desc': 'Gesammelte Artikel',
  'dashboard.dataPool.articlesToday': 'Artikel heute',
  'dashboard.dataPool.pendingAnalysis': 'Ausstehende Analyse',
  'dashboard.dataPool.lastRun': 'Letzter Lauf',
  'dashboard.dataPool.activeTopics': 'Aktive Themen',
  'dashboard.dataPool.articlesCount': '{count} Artikel',
  'dashboard.dataPool.viewArticles': 'Artikel ansehen',
  'dashboard.dataPool.runAnalysis': 'Analyse starten',
  'dashboard.dataPool.runAnalysisTitle':
    'KI-Analyse für heute bereits gescrapte Artikel ohne erneutes Scraping',

  'dashboard.aiGen.title': 'KI-Generierung',
  'dashboard.aiGen.desc': 'Prüft regelmäßig die Abonnenten-Bereitschaft',
  'dashboard.aiGen.subscribers': 'Abonnenten',
  'dashboard.aiGen.waitingDrafts': 'Warte auf neue Artikel für Entwurfsprüfung',
  'dashboard.aiGen.waitingScraper': 'Warte auf Ende des Scrapers…',
  'dashboard.aiGen.runningAnalysis': 'KI-Analyse der Artikel läuft…',
  'dashboard.aiGen.autoCheckEvery': 'Auto-Check',
  'dashboard.aiGen.lastCheck': 'Letzte Prüfung {time}',
  'dashboard.aiGen.autoCheckActive': 'Auto-Check aktiv',
  'dashboard.aiGen.autoCheckIdle': 'Auto-Check inaktiv',
  'dashboard.aiGen.pending': '{count} ausstehend',
  'dashboard.aiGen.noReadiness': 'Noch keine Bereitschaftsdaten',
  'dashboard.aiGen.sentToday': 'Heute gesendet',
  'dashboard.aiGen.draftReady': 'Entwurf bereit',
  'dashboard.aiGen.pendingGeneration': 'Generierung ausstehend',
  'dashboard.aiGen.noTopics': 'Keine Themen',
  'dashboard.aiGen.allReady': 'Alle {count} Abonnenten bereit zum Versand',
  'dashboard.aiGen.someNeedDrafts': '{count} Abonnenten brauchen noch Entwürfe',
  'dashboard.aiGen.pendingShort': 'Ausstehend',

  'dashboard.delivery.title': 'E-Mail-Versand',
  'dashboard.delivery.desc': 'An Abonnenten senden',
  'dashboard.delivery.sendAll': 'Alle senden',
  'dashboard.delivery.individual': 'Einzeln',
  'dashboard.delivery.activeSubscribers': 'Aktive Abonnenten',
  'dashboard.delivery.lastDelivery': 'Letzter Versand',
  'dashboard.delivery.pendingSend': 'Ausstehend',
  'dashboard.delivery.readyCount': '{count} bereit',
  'dashboard.delivery.noSubscribers': 'Keine Abonnenten',
  'dashboard.delivery.sent': '✓ Gesendet',
  'dashboard.delivery.ready': '● Bereit',
  'dashboard.delivery.moreSubscribers': '+{count} weitere Abonnenten',
  'dashboard.delivery.searchPlaceholder': 'Suchen…',
  'dashboard.delivery.selectAll': 'Alle',
  'dashboard.delivery.sendAllNow': 'Jetzt alle senden',
  'dashboard.delivery.sendToOne': 'An 1 Abonnenten senden',
  'dashboard.delivery.sendToMany': 'An {count} Abonnenten senden',

  'dashboard.statSubscribers': 'Abonnenten gesamt',
  'dashboard.statArticlesToday': 'Artikel heute',
  'dashboard.statActiveTopics': 'Aktive Themen',
  'dashboard.statPendingAnalysis': 'Ausstehende Analyse',

  'dashboard.recentTitle': 'Letzte Pipeline-Aktivität',
  'dashboard.recentDesc': 'Ereignisse: Scraping, KI, Versand',

  'dashboard.activityScraperRun': 'Scraper {status} — {count} Artikel gesammelt',

  'dashboard.toastSendAll': 'Sammelversand: {sent} gesendet{failed}{skipped}.',
  'dashboard.toastFailed': ', {n} fehlgeschlagen',
  'dashboard.toastSkipped': ', {n} übersprungen',
  'dashboard.toastSelectOne': 'Mindestens einen Abonnenten auswählen.',
  'dashboard.toastSentToMany': 'Entwurf an {count} Abonnent(en) gesendet.',

  'settings.title': 'Einstellungen',
  'settings.subtitle': 'Scraper-Limits je Kundenvertrag.',
  'settings.clientLabel': 'Moderator / Kunde',
  'settings.clientEditing': 'Vertragslimits für #{id} bearbeiten',
  'settings.moderatorsTitle': 'Moderatoren',
  'settings.moderatorConfigTitle': 'Moderator-Konfiguration',
  'settings.configTitle': 'Scraper-Limit-Einstellungen',
  'settings.sectionAutoQuota': 'Automatische Limits',
  'settings.sectionAutoQuotaDesc': 'Automatische Läufe und Zusatzläufe pro Zeitraum konfigurieren.',
  'settings.sectionDataLimits': 'Datenlimits',
  'settings.sectionDataLimitsDesc': 'Artikel pro URL begrenzen.',
  'settings.sectionStatusNotes': 'Status & Hinweise',
  'settings.sectionStatusNotesDesc': 'Scraper ein-/ausschalten und Änderungsgrund notieren.',
  'settings.searchPlaceholder': 'Nach Name oder E-Mail suchen…',
  'settings.noSearchResults': 'Keine Moderatoren entsprechen Ihrer Suche.',
  'settings.selectModerator': 'Wählen Sie einen Moderator, um Scraper-Limits zu konfigurieren.',
  'settings.statusActive': 'Aktiv',
  'settings.statusInactive': 'Inaktiv',
  'settings.clientMeta': 'Kunde #{id}',
  'settings.autoRunsPerDay': 'Automatische Läufe / Tag',
  'settings.extraRunsPerPeriod': 'Zusatzläufe / Zeitraum',
  'settings.extraPeriod': 'Zusatzzeitraum',
  'settings.maxArticlesPerUrl': 'Max. Artikel / URL (1–200)',
  'settings.maxUrlsPerRun': 'Max. URLs / Moderator-Lauf',
  'settings.scraperEnabled': 'Scraper aktiviert',
  'settings.scraperEnabledDesc': 'Erlaubt diesem Kunden, den Scraper überhaupt auszuführen.',
  'settings.changeReason': 'Änderungsgrund',
  'settings.changeReasonPlaceholder': 'Vertragsänderung…',
  'settings.saveConfig': 'Konfiguration speichern',
  'settings.cancel': 'Abbrechen',
  'settings.toastConfigSaved': 'Scraper-Limits gespeichert',
  'settings.errMaxArticles': 'Maximale Artikel pro URL müssen zwischen 1 und 200 liegen.',
  'settings.loadFailed': 'Kunden konnten nicht geladen werden',
  'settings.noClients': 'Noch keine Kunden gefunden. Legen Sie zuerst einen Moderator an.',
  'settings.optional': '(optional)',
  'settings.helpAutoRuns': 'Geplante automatische Scrapes pro Kalendertag.',
  'settings.helpExtraRuns': 'Zusätzliche manuelle Läufe im gewählten Zeitraum.',
  'settings.helpExtraPeriod': 'Zurücksetzungsfenster für Zusatzläufe.',
  'settings.helpMaxUrls': 'Obergrenze der URLs in einem Moderator-Lauf.',
  'settings.helpMaxArticles': 'Obergrenze der Artikel pro URL. Muss zwischen 1 und 200 liegen.',
  'settings.helpChangeReason': 'Optionaler Hinweis zu dieser Konfigurationsänderung.',
  'settings.period.daily': 'Täglich',
  'settings.period.weekly': 'Wöchentlich',
  'settings.period.monthly': 'Monatlich',
  'settings.paginationRange': '{start}–{end} von {total}',
  'settings.prevPage': 'Vorherige Seite',
  'settings.nextPage': 'Nächste Seite',

};

const vi: Record<string, string> = {
  'sidebar.brand': 'Hawk',
  'sidebar.logOut': 'Đăng xuất',
  'sidebar.expandSidebar': 'Mở rộng thanh bên',
  'sidebar.collapseSidebar': 'Thu gọn thanh bên',
  'sidebar.language': 'Ngôn ngữ',

  'nav.dashboard': 'Bảng điều khiển',
  'nav.moderators': 'Người kiểm duyệt',
  'nav.settings': 'Usages Settings',
  'nav.aiManager': 'Quản lý AI',
  'nav.mailOverview': 'Tổng quan thư',
  'nav.scraperPool': 'Nguồn scraper',
  'nav.dataPool': 'Kho dữ liệu',
  'nav.subscribers': 'Người đăng ký',
  'nav.emailTemplates': 'Mẫu email',
  'nav.myNewsletters': 'Newsletter của tôi',
  'nav.dataroomSetting': 'Cài đặt Dataroom',
  'nav.linkedin': 'LinkedIn',
  'nav.linkedinScheduled': 'LinkedIn đã lên lịch',
  'nav.section.administration': 'Quản trị',
  'nav.section.editorial': 'Biên tập',
  'nav.section.yourAccount': 'Tài khoản của bạn',

  'locale.en': 'Tiếng Anh',
  'locale.de': 'Tiếng Đức',
  'locale.vi': 'Tiếng Việt',
  'locale.fr': 'Tiếng Pháp',

  'role.admin': 'Quản trị',
  'role.moderator': 'Kiểm duyệt',
  'role.subscriber': 'Người đăng ký',

  'dashboard.title': 'Bảng điều khiển',
  'dashboard.subtitle': 'Thu thập → Tổng hợp → AI → Gửi',
  'dashboard.pipeline': 'Pipeline',
  'dashboard.progress': 'Tiến độ',
  'dashboard.stepOf': 'Bước {n} / {total}',
  'dashboard.triggerDrafts': 'Kích hoạt bản nháp',
  'dashboard.requestFailed': 'Yêu cầu thất bại',
  'dashboard.badgeScheduled': 'Đã lên lịch {time}',
  'dashboard.badgeScraperRunning': 'Scraper đang chạy',
  'dashboard.badgeAnalyzing': 'Đang phân tích bài viết',
  'dashboard.badgeCheckingReadiness': 'Đang kiểm tra người đăng ký',
  'dashboard.badgeAutoCheckArmed': 'Tự kiểm tra sẵn sàng',
  'dashboard.badgeEmailsReady': 'Email sẵn sàng',

  'dashboard.step': 'Bước {n}',
  'dashboard.scraperPool.title': 'Nguồn scraper',
  'dashboard.scraperPool.desc': 'Tự chạy hằng ngày lúc {time}',
  'dashboard.scraperPool.activeUrls': 'URL đang hoạt động',
  'dashboard.scraperPool.scheduled': 'Lịch',
  'dashboard.scraperPool.daily': '{time} mỗi ngày',
  'dashboard.scraperPool.lastRun': 'Lần chạy cuối',
  'dashboard.scraperPool.today': 'Hôm nay {time}',
  'dashboard.scraperPool.more': '+{count} nữa',
  'dashboard.scraperPool.noSources': 'Chưa có nguồn hoạt động',
  'dashboard.scraperPool.scraping': 'Đang thu thập {count} bài…',
  'dashboard.scraperPool.completed': 'Hoàn tất — {count} bài đã thu',
  'dashboard.scraperPool.manage': 'Quản lý',
  'dashboard.scraperPool.runNow': 'Chạy ngay',

  'dashboard.dataPool.title': 'Kho dữ liệu',
  'dashboard.dataPool.desc': 'Bài viết đã thu thập',
  'dashboard.dataPool.articlesToday': 'Bài hôm nay',
  'dashboard.dataPool.pendingAnalysis': 'Chờ phân tích',
  'dashboard.dataPool.lastRun': 'Lần chạy cuối',
  'dashboard.dataPool.activeTopics': 'Chủ đề hoạt động',
  'dashboard.dataPool.articlesCount': '{count} bài',
  'dashboard.dataPool.viewArticles': 'Xem bài',
  'dashboard.dataPool.runAnalysis': 'Chạy phân tích',
  'dashboard.dataPool.runAnalysisTitle':
    'Chạy phân tích AI cho bài đã thu hôm nay, không thu lại',

  'dashboard.aiGen.title': 'Sinh nội dung AI',
  'dashboard.aiGen.desc': 'Kiểm tra định kỳ người đăng ký',
  'dashboard.aiGen.subscribers': 'Người đăng ký',
  'dashboard.aiGen.waitingDrafts': 'Đợi bài mới để kiểm tra bản nháp',
  'dashboard.aiGen.waitingScraper': 'Đợi scraper hoàn tất…',
  'dashboard.aiGen.runningAnalysis': 'Đang phân tích AI…',
  'dashboard.aiGen.autoCheckEvery': 'Tự kiểm tra',
  'dashboard.aiGen.lastCheck': 'Lần kiểm tra {time}',
  'dashboard.aiGen.autoCheckActive': 'Tự kiểm tra đang bật',
  'dashboard.aiGen.autoCheckIdle': 'Tự kiểm tra đang tắt',
  'dashboard.aiGen.pending': '{count} đang chờ',
  'dashboard.aiGen.noReadiness': 'Chưa có dữ liệu sẵn sàng',
  'dashboard.aiGen.sentToday': 'Đã gửi hôm nay',
  'dashboard.aiGen.draftReady': 'Bản nháp sẵn sàng',
  'dashboard.aiGen.pendingGeneration': 'Chờ tạo',
  'dashboard.aiGen.noTopics': 'Không có chủ đề',
  'dashboard.aiGen.allReady': 'Cả {count} người đăng ký sẵn sàng gửi',
  'dashboard.aiGen.someNeedDrafts': '{count} người cần bản nháp',
  'dashboard.aiGen.pendingShort': 'Đang chờ',

  'dashboard.delivery.title': 'Gửi email',
  'dashboard.delivery.desc': 'Gửi cho người đăng ký',
  'dashboard.delivery.sendAll': 'Gửi tất cả',
  'dashboard.delivery.individual': 'Từng người',
  'dashboard.delivery.activeSubscribers': 'Người đăng ký hoạt động',
  'dashboard.delivery.lastDelivery': 'Lần gửi cuối',
  'dashboard.delivery.pendingSend': 'Chờ gửi',
  'dashboard.delivery.readyCount': '{count} sẵn sàng',
  'dashboard.delivery.noSubscribers': 'Không có người đăng ký',
  'dashboard.delivery.sent': '✓ Đã gửi',
  'dashboard.delivery.ready': '● Sẵn sàng',
  'dashboard.delivery.moreSubscribers': '+{count} người nữa',
  'dashboard.delivery.searchPlaceholder': 'Tìm…',
  'dashboard.delivery.selectAll': 'Tất cả',
  'dashboard.delivery.sendAllNow': 'Gửi tất cả ngay',
  'dashboard.delivery.sendToOne': 'Gửi cho 1 người',
  'dashboard.delivery.sendToMany': 'Gửi cho {count} người',

  'dashboard.statSubscribers': 'Tổng người đăng ký',
  'dashboard.statArticlesToday': 'Bài hôm nay',
  'dashboard.statActiveTopics': 'Chủ đề hoạt động',
  'dashboard.statPendingAnalysis': 'Chờ phân tích',

  'dashboard.recentTitle': 'Hoạt động pipeline gần đây',
  'dashboard.recentDesc': 'Scraping, AI và gửi mail',

  'dashboard.activityScraperRun': 'Scraper {status} — {count} bài đã thu',

  'dashboard.toastSendAll': 'Gửi hàng loạt: {sent} đã gửi{failed}{skipped}.',
  'dashboard.toastFailed': ', {n} lỗi',
  'dashboard.toastSkipped': ', {n} bỏ qua',
  'dashboard.toastSelectOne': 'Chọn ít nhất một người đăng ký.',
  'dashboard.toastSentToMany': 'Đã gửi bản nháp tới {count} người.',

  'settings.title': 'Cài đặt',
  'settings.subtitle': 'Cấu hình giới hạn scraper theo hợp đồng khách hàng.',
  'settings.clientLabel': 'Người kiểm duyệt / khách hàng',
  'settings.clientEditing': 'Đang chỉnh giới hạn hợp đồng cho #{id}',
  'settings.moderatorsTitle': 'Người kiểm duyệt',
  'settings.moderatorConfigTitle': 'Cấu hình người kiểm duyệt',
  'settings.configTitle': 'Cài đặt giới hạn Scraper',
  'settings.sectionAutoQuota': 'Hạn mức tự động',
  'settings.sectionAutoQuotaDesc': 'Cấu hình số lượt tự động và lượt thêm theo kỳ.',
  'settings.sectionDataLimits': 'Giới hạn dữ liệu',
  'settings.sectionDataLimitsDesc': 'Giới hạn số bài mỗi URL.',
  'settings.sectionStatusNotes': 'Trạng thái & ghi chú',
  'settings.sectionStatusNotesDesc': 'Bật/Tắt scraper và ghi chú lý do thay đổi.',
  'settings.searchPlaceholder': 'Tìm theo tên hoặc email…',
  'settings.noSearchResults': 'Không có người kiểm duyệt khớp tìm kiếm.',
  'settings.selectModerator': 'Chọn một người kiểm duyệt để cấu hình giới hạn scraper.',
  'settings.statusActive': 'Hoạt động',
  'settings.statusInactive': 'Không hoạt động',
  'settings.clientMeta': 'Khách hàng #{id}',
  'settings.autoRunsPerDay': 'Số lượt tự động / ngày',
  'settings.extraRunsPerPeriod': 'Số lượt thêm / kỳ',
  'settings.extraPeriod': 'Kỳ tính lượt thêm',
  'settings.maxArticlesPerUrl': 'Số bài tối đa / URL (1–200)',
  'settings.maxUrlsPerRun': 'Số URL tối đa / lượt chạy',
  'settings.scraperEnabled': 'Bật scraper',
  'settings.scraperEnabledDesc': 'Cho phép khách hàng này chạy scraper.',
  'settings.changeReason': 'Lý do thay đổi',
  'settings.changeReasonPlaceholder': 'Sửa đổi hợp đồng…',
  'settings.saveConfig': 'Lưu cấu hình',
  'settings.cancel': 'Huỷ',
  'settings.toastConfigSaved': 'Đã lưu giới hạn scraper',
  'settings.errMaxArticles': 'Số bài tối đa mỗi URL phải từ 1 đến 200.',
  'settings.loadFailed': 'Không tải được danh sách khách hàng',
  'settings.noClients': 'Chưa có khách hàng nào. Hãy thêm người kiểm duyệt trước.',
  'settings.optional': '(tuỳ chọn)',
  'settings.helpAutoRuns': 'Số lượt scrape tự động được phép mỗi ngày.',
  'settings.helpExtraRuns': 'Số lượt chạy thủ công bổ sung trong kỳ đã chọn.',
  'settings.helpExtraPeriod': 'Chu kỳ đặt lại hạn mức lượt thêm.',
  'settings.helpMaxUrls': 'Giới hạn số URL trong một lượt chạy của người kiểm duyệt.',
  'settings.helpMaxArticles': 'Giới hạn số bài crawl mỗi URL. Phải từ 1 đến 200.',
  'settings.helpChangeReason': 'Ghi chú tuỳ chọn kèm theo thay đổi cấu hình này.',
  'settings.period.daily': 'Hàng ngày',
  'settings.period.weekly': 'Hàng tuần',
  'settings.period.monthly': 'Hàng tháng',
  'settings.paginationRange': '{start}–{end} / {total}',
  'settings.prevPage': 'Trang trước',
  'settings.nextPage': 'Trang sau',

};

const fr: Record<string, string> = {
  'sidebar.brand': 'Hawk',
  'sidebar.logOut': 'Déconnexion',
  'sidebar.expandSidebar': 'Développer la barre latérale',
  'sidebar.collapseSidebar': 'Réduire la barre latérale',
  'sidebar.language': 'Langue',

  'nav.dashboard': 'Tableau de bord',
  'nav.moderators': 'Modérateurs',
  'nav.settings': 'Usages Settings',
  'nav.aiManager': 'Gestionnaire IA',
  'nav.mailOverview': 'Aperçu des e-mails',
  'nav.scraperPool': 'Pool de scrapers',
  'nav.dataPool': 'Pool de données',
  'nav.subscribers': 'Abonnés',
  'nav.emailTemplates': 'Modèles d\'e-mail',
  'nav.myNewsletters': 'Mes newsletters',
  'nav.dataroomSetting': 'Paramètres Dataroom',
  'nav.linkedin': 'LinkedIn',
  'nav.linkedinScheduled': 'LinkedIn planifié',
  'nav.section.administration': 'Administration',
  'nav.section.editorial': 'Éditorial',
  'nav.section.yourAccount': 'Votre compte',

  'locale.en': 'Anglais',
  'locale.de': 'Allemand',
  'locale.vi': 'Vietnamien',
  'locale.fr': 'Français',

  'role.admin': 'Admin',
  'role.moderator': 'Modérateur',
  'role.subscriber': 'Abonné',

  'dashboard.title': 'Tableau de bord',
  'dashboard.subtitle': 'Scraping → Collecte → IA → Envoi',
  'dashboard.pipeline': 'Pipeline',
  'dashboard.progress': 'Progression',
  'dashboard.stepOf': 'Étape {n} sur {total}',
  'dashboard.triggerDrafts': 'Déclencher les brouillons',
  'dashboard.requestFailed': 'Échec de la requête',
  'dashboard.badgeScheduled': 'Planifié {time}',
  'dashboard.badgeScraperRunning': 'Scraper en cours',
  'dashboard.badgeAnalyzing': 'Analyse des articles',
  'dashboard.badgeCheckingReadiness': 'Vérification des abonnés',
  'dashboard.badgeAutoCheckArmed': 'Auto-vérification active',
  'dashboard.badgeEmailsReady': 'E-mails prêts',

  'dashboard.step': 'Étape {n}',
  'dashboard.scraperPool.title': 'Pool de scrapers',
  'dashboard.scraperPool.desc': 'Exécution automatique à {time} chaque jour',
  'dashboard.scraperPool.activeUrls': 'URL actives',
  'dashboard.scraperPool.scheduled': 'Planifié',
  'dashboard.scraperPool.daily': '{time} par jour',
  'dashboard.scraperPool.lastRun': 'Dernière exécution',
  'dashboard.scraperPool.today': 'Aujourd\'hui {time}',
  'dashboard.scraperPool.more': '+{count} de plus',
  'dashboard.scraperPool.noSources': 'Aucune source active',
  'dashboard.scraperPool.scraping': 'Collecte de {count} articles…',
  'dashboard.scraperPool.completed': 'Terminé — {count} articles collectés',
  'dashboard.scraperPool.manage': 'Gérer',
  'dashboard.scraperPool.runNow': 'Lancer maintenant',

  'dashboard.dataPool.title': 'Pool de données',
  'dashboard.dataPool.desc': 'Articles collectés',
  'dashboard.dataPool.articlesToday': 'Articles aujourd\'hui',
  'dashboard.dataPool.pendingAnalysis': 'Analyse en attente',
  'dashboard.dataPool.lastRun': 'Dernière exécution',
  'dashboard.dataPool.activeTopics': 'Sujets actifs',
  'dashboard.dataPool.articlesCount': '{count} articles',
  'dashboard.dataPool.viewArticles': 'Voir les articles',
  'dashboard.dataPool.runAnalysis': 'Lancer l\'analyse',
  'dashboard.dataPool.runAnalysisTitle':
    'Lancer l\'analyse IA sur les articles déjà collectés aujourd\'hui sans re-scraper',

  'dashboard.aiGen.title': 'Génération IA',
  'dashboard.aiGen.desc': 'Vérifie régulièrement la disponibilité des abonnés',
  'dashboard.aiGen.subscribers': 'Abonnés',
  'dashboard.aiGen.waitingDrafts': 'En attente de nouveaux articles pour vérifier les brouillons',
  'dashboard.aiGen.waitingScraper': 'En attente de la fin du scraper…',
  'dashboard.aiGen.runningAnalysis': 'Analyse IA des articles en cours…',
  'dashboard.aiGen.autoCheckEvery': 'Auto-vérification',
  'dashboard.aiGen.lastCheck': 'Dernière vérification {time}',
  'dashboard.aiGen.autoCheckActive': 'Auto-vérification active',
  'dashboard.aiGen.autoCheckIdle': 'Auto-vérification inactive',
  'dashboard.aiGen.pending': '{count} en attente',
  'dashboard.aiGen.noReadiness': 'Aucune donnée de disponibilité',
  'dashboard.aiGen.sentToday': 'Envoyés aujourd\'hui',
  'dashboard.aiGen.draftReady': 'Brouillon prêt',
  'dashboard.aiGen.pendingGeneration': 'Génération en attente',
  'dashboard.aiGen.noTopics': 'Aucun sujet',
  'dashboard.aiGen.allReady': 'Les {count} abonnés sont prêts à recevoir',
  'dashboard.aiGen.someNeedDrafts': '{count} abonnés ont encore besoin de brouillons',
  'dashboard.aiGen.pendingShort': 'En attente',

  'dashboard.delivery.title': 'Envoi d\'e-mails',
  'dashboard.delivery.desc': 'Envoyer aux abonnés',
  'dashboard.delivery.sendAll': 'Tout envoyer',
  'dashboard.delivery.individual': 'Individuel',
  'dashboard.delivery.activeSubscribers': 'Abonnés actifs',
  'dashboard.delivery.lastDelivery': 'Dernier envoi',
  'dashboard.delivery.pendingSend': 'Envoi en attente',
  'dashboard.delivery.readyCount': '{count} prêts',
  'dashboard.delivery.noSubscribers': 'Aucun abonné trouvé',
  'dashboard.delivery.sent': '✓ Envoyé',
  'dashboard.delivery.ready': '● Prêt',
  'dashboard.delivery.moreSubscribers': '+{count} abonnés de plus',
  'dashboard.delivery.searchPlaceholder': 'Rechercher…',
  'dashboard.delivery.selectAll': 'Tous',
  'dashboard.delivery.sendAllNow': 'Tout envoyer maintenant',
  'dashboard.delivery.sendToOne': 'Envoyer à 1 abonné',
  'dashboard.delivery.sendToMany': 'Envoyer à {count} abonnés',

  'dashboard.statSubscribers': 'Total abonnés',
  'dashboard.statArticlesToday': 'Articles aujourd\'hui',
  'dashboard.statActiveTopics': 'Sujets actifs',
  'dashboard.statPendingAnalysis': 'Analyse en attente',

  'dashboard.recentTitle': 'Activité récente du pipeline',
  'dashboard.recentDesc': 'Derniers événements : scraping, IA et envoi',

  'dashboard.activityScraperRun': 'Scraper {status} — {count} articles collectés',

  'dashboard.toastSendAll': 'Envoi groupé : {sent} envoyés{failed}{skipped}.',
  'dashboard.toastFailed': ', {n} échoués',
  'dashboard.toastSkipped': ', {n} ignorés',
  'dashboard.toastSelectOne': 'Sélectionnez au moins un abonné.',
  'dashboard.toastSentToMany': 'Brouillon envoyé à {count} abonné(s).',

  'settings.title': 'Paramètres',
  'settings.subtitle': 'Configuration des limites de scraper par contrat client.',
  'settings.clientLabel': 'Modérateur / client',
  'settings.clientEditing': 'Modification des limites du contrat #{id}',
  'settings.moderatorsTitle': 'Modérateurs',
  'settings.moderatorConfigTitle': 'Configuration du modérateur',
  'settings.configTitle': 'Paramètres de limite du scraper',
  'settings.sectionAutoQuota': 'Limites automatiques',
  'settings.sectionAutoQuotaDesc': 'Configurer les exécutions automatiques et supplémentaires par période.',
  'settings.sectionDataLimits': 'Limites de données',
  'settings.sectionDataLimitsDesc': 'Limiter le nombre d\'articles par URL.',
  'settings.sectionStatusNotes': 'Statut & notes',
  'settings.sectionStatusNotesDesc': 'Activer/désactiver le scraper et noter la raison du changement.',
  'settings.searchPlaceholder': 'Rechercher par nom ou e-mail…',
  'settings.noSearchResults': 'Aucun modérateur ne correspond à votre recherche.',
  'settings.selectModerator': 'Sélectionnez un modérateur pour configurer les limites.',
  'settings.statusActive': 'Actif',
  'settings.statusInactive': 'Inactif',
  'settings.clientMeta': 'Client #{id}',
  'settings.autoRunsPerDay': 'Exécutions automatiques / jour',
  'settings.extraRunsPerPeriod': 'Exécutions supplémentaires / période',
  'settings.extraPeriod': 'Période supplémentaire',
  'settings.maxArticlesPerUrl': 'Articles max / URL (1–200)',
  'settings.maxUrlsPerRun': 'URL max / exécution du modérateur',
  'settings.scraperEnabled': 'Scraper activé',
  'settings.scraperEnabledDesc': 'Autoriser ce client à exécuter le scraper.',
  'settings.changeReason': 'Motif du changement',
  'settings.changeReasonPlaceholder': 'Avenant au contrat…',
  'settings.saveConfig': 'Enregistrer la configuration',
  'settings.cancel': 'Annuler',
  'settings.toastConfigSaved': 'Limites du scraper enregistrées',
  'settings.errMaxArticles': 'Le nombre maximal d\'articles par URL doit être compris entre 1 et 200.',
  'settings.loadFailed': 'Échec du chargement des clients',
  'settings.noClients': 'Aucun client trouvé. Ajoutez d\'abord un modérateur.',
  'settings.optional': '(facultatif)',
  'settings.helpAutoRuns': 'Exécutions automatiques planifiées autorisées par jour.',
  'settings.helpExtraRuns': 'Exécutions manuelles supplémentaires dans la période sélectionnée.',
  'settings.helpExtraPeriod': 'Fenêtre de réinitialisation des exécutions supplémentaires.',
  'settings.helpMaxUrls': 'Limite d\'URL soumises en une seule exécution.',
  'settings.helpMaxArticles': 'Plafond d\'articles crawlés par URL. Doit être entre 1 et 200.',
  'settings.helpChangeReason': 'Note facultative enregistrée avec ce changement.',
  'settings.period.daily': 'Quotidien',
  'settings.period.weekly': 'Hebdomadaire',
  'settings.period.monthly': 'Mensuel',
  'settings.paginationRange': '{start}–{end} sur {total}',
  'settings.prevPage': 'Page précédente',
  'settings.nextPage': 'Page suivante',
};

function mergeMessages(
  base: Record<string, string>,
  extra: Record<string, string>,
): Record<string, string> {
  return { ...base, ...extra };
}

export const UI_MESSAGES: Record<UiLocale, Record<string, string>> = {
  en: mergeMessages(en, MODERATOR_PAGE_MESSAGES.en),
  de: mergeMessages(de, MODERATOR_PAGE_MESSAGES.de),
  vi: mergeMessages(vi, MODERATOR_PAGE_MESSAGES.vi),
  fr: mergeMessages(fr, MODERATOR_PAGE_MESSAGES.fr),
};

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  let s = template;
  for (const [k, v] of Object.entries(vars)) {
    s = s.split(`{${k}}`).join(String(v));
  }
  return s;
}

export function translate(
  locale: UiLocale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const table = UI_MESSAGES[locale] ?? UI_MESSAGES.en;
  const fallback = UI_MESSAGES.en[key];
  const raw = table[key] ?? fallback ?? key;
  return interpolate(raw, vars);
}
